// Walking bass-line generation, after Ed Friedland's *Building Walking Bass
// Lines*. Pure and framework-independent (unit-tested); no React, no audio.
//
// Friedland's method is a progressive ladder — each rung adds one device on top
// of the last. The `BassStyle` selector exposes that ladder directly:
//   roots          → the root on each chord (the bottom of every line)
//   roots-fifths   → roots, fifths and octaves (the "two/four" foundation)
//   chromatic      → + a half-step approach into each new chord (U/chr, L/chr)
//   dominant       → + a fifth-of-the-target approach (U/dom, L/dom)
//   scale          → + a scale-tone approach (sc)
//   walking        → the real thing: root on the downbeat, chord tones walked
//                    on the inner beats, and the *smoothest* of the three
//                    approaches on the beat before each chord change.
//   advanced       → Part Two: walking, but each chord change is set up by a
//                    two-note *indirect resolution* (an enclosure) — one of
//                    Friedland's five chromatic/scalar surrounding patterns.
//
// The chord's bass target anchors each change, and the last walking beat sets
// up the next target. An explicit repeated slash-bass pedal is the exception:
// keep its bass steady while the upper harmony changes. Lines stay in a low
// register and move by small steps — "like going up and down a flight of stairs."

import { type ChordTypeId, getChordType } from './chords';
import type { PitchClass } from './notes';
import { tensionParent } from './tensions';

export type BassStyle =
  | 'roots'
  | 'roots-fifths'
  | 'chromatic'
  | 'dominant'
  | 'scale'
  | 'walking'
  | 'advanced'
  | 'shuffle'
  | 'latin';
export type BassFeel = 'two' | 'four';

/** The style ladder, in book order, with UI labels and one-line descriptions. */
export const BASS_STYLES: { id: BassStyle; label: string; hint: string }[] = [
  { id: 'roots', label: 'Roots', hint: 'Root (and octave) on each chord' },
  { id: 'roots-fifths', label: 'Roots + 5ths', hint: 'Roots, fifths and octaves' },
  { id: 'chromatic', label: '+ Chromatic approach', hint: 'Half-step approach into each chord' },
  { id: 'dominant', label: '+ Dominant approach', hint: 'Approach a chord from its fifth' },
  { id: 'scale', label: '+ Scale approach', hint: 'Approach from the next scale tone' },
  { id: 'walking', label: 'Full walking', hint: 'Chord tones walked, smartest approach into each change' },
  { id: 'advanced', label: 'Advanced (enclosures)', hint: 'Two-note indirect resolutions around each target' },
  // Not part of the Friedland ladder: groove styles with their own rhythm
  // (they ignore the two/four feel switch).
  { id: 'shuffle', label: 'Boogie shuffle', hint: 'Classic R-3-5-6 boogie figure — blues shuffle bass' },
  { id: 'latin', label: 'Bossa / Latin', hint: 'Root-and-fifth tumbao; the last eighth anticipates the change (plays straight)' },
];

/** Minimal chord description the generator needs (one per performance beat-group). */
export interface BassInputChord {
  rootPc: PitchClass;
  chordType: ChordTypeId;
  /** The slash bass, when the symbol names one ("C" in "D/C"). A slash chord
   *  names its own bottom note, and the bass is what plays the bottom: the
   *  chord's DOWNBEAT sounds this instead of the root, and an approach into
   *  the chord aims at it. A single inversion still walks the chord; a
   *  contiguous run of changing harmonies explicitly over the same bass
   *  establishes a pedal which stays on that pitch throughout. */
  bassPc?: PitchClass;
  /** Absolute 0-based beat onset from the start of the form. */
  startBeat: number;
  /** How many beats this chord occupies. */
  durationBeats: number;
}

export interface BassNote {
  /** Bass-register MIDI pitch. */
  midi: number;
  /** Absolute beat onset (0-based; fractional once embellished). */
  beat: number;
  durationBeats: number;
  /** Friedland analysis label: R, 3, 5, 8, ♭7, U/chr, L/dom, sc … or Pedal. */
  label: string;
  /** Note loudness 0–1 (1 = normal; ghost/dead notes are quieter). Default 1. */
  velocity?: number;
  /** Sound this fraction of the note and stop (a skip or a ghost is short);
   *  absent = legato, ringing until the next pluck. */
  articulationGate?: number;
}

export interface WalkingBassOptions {
  style: BassStyle;
  feel: BassFeel;
  /** Per-pass seed: 0 (or omitted) is the canonical line; other values nudge a
   * few walking-note choices so each repeat of the form differs slightly. */
  variation?: number;
  /**
   * Per-chord realization, for a tune whose feel changes mid-form (#174):
   * given a chord's start beat, the style and feel to play it in. Anything
   * omitted falls back to the global choice.
   *
   * Resolved INSIDE the single walk rather than by generating each segment
   * separately, which is the whole point: `prev` carries across a boundary and
   * the approach into the next chord is the real next chord, so a Latin→Swing
   * change is a change of figure, not a restart. Generating segments in
   * isolation would anchor each one's first root low and loop-close each
   * one's last approach back to its own first chord.
   */
  styleAt?: (startBeat: number) => { style?: BassStyle; feel?: BassFeel } | undefined;
}

/**
 * The rhythm-section convention, as a `styleAt` feel source: heads in 2,
 * solo choruses walking in 4, the head out back in 2 (the classic bookend).
 * Pure — pass the performance plan's pass starts (in beats) and kinds and it
 * maps any chord onset to the feel its pass plays in. Returns undefined for
 * a 'written' pass (the as-written reading has no head/solo distinction), so
 * the player's chosen feel stands. Corpus-audited before building: this is
 * never notated (0 feel markings in the openbook 151; 1 in 1460 iReal charts,
 * and that one is Brilliant Corners' composed double-time repeat), so it is
 * automated from the itinerary rather than read from the sheet.
 */
export function formFeelAt(
  passStarts: readonly number[],
  passKinds: readonly ('head' | 'solo' | 'head-out' | 'written')[],
): (startBeat: number) => BassFeel | undefined {
  return (startBeat) => {
    let pass = 0;
    for (let i = 0; i < passStarts.length; i += 1) {
      if (startBeat >= passStarts[i]) pass = i;
    }
    const kind = passKinds[pass];
    if (kind === 'head' || kind === 'head-out') return 'two';
    if (kind === 'solo') return 'four';
    return undefined;
  };
}

/** Roughly how often a walking beat takes its second-nearest chord tone instead
 * of the nearest, when variation is on — enough to feel alive, not to wander. */
const WALK_VARY_PROB = 0.32;

// Electric-bass register the line lives in: low E (E1) up to E3, two octaves.
const BASS_LOW = 28; // E1
const BASS_HIGH = 52; // E3
// Roots are anchored low so the line sits where a real bass sits.
const ANCHOR_LOW = 33; // A1

/** Place a pitch class in the octave nearest `ref`, clamped to the bass register. */
function octaveNearest(pc: PitchClass, ref: number): number {
  let m = pc + 12 * Math.round((ref - pc) / 12);
  while (m < BASS_LOW) m += 12;
  while (m > BASS_HIGH) m -= 12;
  return m;
}

/**
 * The harmonic chord tones a bass line walks (root, 3rd, 5th, 7th) as semitone
 * offsets from the root. Derived from the catalogue's chord-tone formula so
 * altered fifths (♭5/♯5), the diminished ♭♭7, and 6ths all fall out correctly —
 * note this is *not* the drop-2 voicing (which drops roots on ninth chords).
 * A triad names no seventh, so it walks as the seventh chord it stands for
 * (tensions.ts: maj → maj7, aug → maj7♯5, dim → °7, each sharing the triad's
 * third and fifth), as the importers voice a bare triad; read from its own
 * formula it fell to the ♭7 default and a major triad walked as a dominant.
 */
export function bassChordTones(typeId: ChordTypeId): {
  third: number;
  fifth: number;
  seventh: number;
} {
  const tokens = getChordType(tensionParent(typeId)).formula.split(/\s+/);
  const has = (t: string) => tokens.includes(t);
  const third = has('♭3') ? 3 : has('4') ? 5 : 4; // ♭3, or 4 for sus, else 3
  const fifth = has('♭5') ? 6 : has('♯5') ? 8 : 7;
  const seventh = has('♭♭7') ? 9 : has('♭7') ? 10 : has('7') ? 11 : has('6') ? 9 : 10;
  return { third, fifth, seventh };
}

/**
 * The scale used to fill between chord tones (scalewise motion / scale approach).
 * Major→Ionian, dominant→Mixolydian, minor→Dorian, diminished→whole-half — each
 * adjusted so its 5th matches the chord's actual (possibly altered) fifth. A
 * triad takes the scale of the chord it stands for, so a major triad never
 * walks a Dorian scale with its major third added on top.
 */
export function bassScale(typeId: ChordTypeId): number[] {
  const cat = getChordType(tensionParent(typeId)).category;
  const { third, fifth, seventh } = bassChordTones(typeId);
  let base: number[];
  if (cat === 'major') base = [0, 2, 4, 5, 7, 9, 11];
  else if (cat === 'dominant') base = [0, 2, 4, 5, 7, 9, 10];
  else if (cat === 'diminished') base = [0, 2, 3, 5, 6, 8, 9, 11];
  else base = [0, 2, 3, 5, 7, 9, 10]; // minor → Dorian
  const set = new Set(base);
  if (fifth !== 7) {
    set.delete(7);
    set.add(fifth);
  }
  set.add(0);
  set.add(third);
  set.add(seventh);
  return [...set].sort((a, b) => a - b);
}

interface ToneSet {
  /** What the chord sounds at the bottom: the slash bass, else the root. */
  bass: PitchClass;
  root: PitchClass;
  third: PitchClass;
  fifth: PitchClass;
  seventh: PitchClass;
  seventhLabel: string;
}

/** What the chord sounds at the bottom: its slash bass, else its root. */
function soundingBass(chord: BassInputChord): PitchClass {
  return ((chord.bassPc ?? chord.rootPc) % 12) as PitchClass;
}

function toneSet(chord: BassInputChord): ToneSet {
  const t = bassChordTones(chord.chordType);
  return {
    bass: soundingBass(chord),
    root: chord.rootPc % 12,
    third: (chord.rootPc + t.third) % 12,
    fifth: (chord.rootPc + t.fifth) % 12,
    seventh: (chord.rootPc + t.seventh) % 12,
    seventhLabel: t.seventh === 9 ? '6' : t.seventh === 11 ? '7' : '♭7',
  };
}

interface Approach {
  pc: PitchClass;
  label: string;
}
interface PlacedNote {
  midi: number;
  label: string;
}

/**
 * Place the smoothest of several approach candidates: the smallest leap from the
 * previous note wins, but never repeat the previous note when another option
 * exists (a held note is a dead spot in a walking line). Candidate order breaks
 * ties, so callers list their preferred device first.
 */
function pickApproach(candidates: Approach[], prev: number): PlacedNote {
  let best: PlacedNote | null = null;
  let bestLeap = Infinity;
  let repeat: PlacedNote | null = null;
  let repeatLeap = Infinity;
  for (const c of candidates) {
    const midi = octaveNearest(c.pc, prev);
    const leap = Math.abs(midi - prev);
    if (midi === prev) {
      if (leap < repeatLeap) {
        repeatLeap = leap;
        repeat = { midi, label: c.label };
      }
      continue;
    }
    if (leap < bestLeap) {
      bestLeap = leap;
      best = { midi, label: c.label };
    }
  }
  return best ?? repeat ?? { midi: octaveNearest(candidates[0].pc, prev), label: candidates[0].label };
}

/** Half-step approach candidates: from below (L/chr) and above (U/chr). */
function chromaticCands(targetPc: PitchClass): Approach[] {
  return [
    { pc: (targetPc + 11) % 12, label: 'L/chr' }, // leading tone preferred on ties
    { pc: (targetPc + 1) % 12, label: 'U/chr' },
  ];
}

/** Dominant approach: the fifth of the target, labelled by which octave it lands in. */
function dominantApproach(targetPc: PitchClass, prev: number): PlacedNote {
  const dom = (targetPc + 7) % 12; // the dominant (fifth) of the target
  let midi = octaveNearest(dom, prev);
  if (midi === prev) midi = midi + 12 <= BASS_HIGH ? midi + 12 : midi - 12; // dodge a repeat
  const target = octaveNearest(targetPc, prev);
  return { midi, label: midi >= target ? 'U/dom' : 'L/dom' };
}

/** Scale-tone approach candidates: the steps either side of the target that
 *  belong to the CURRENT chord's scale. The TARGET is what the next chord
 *  sounds at the bottom (its slash bass where it has one), but the approach
 *  sounds under the chord it leaves, so it has to fit that chord. Taken from
 *  the target's own scale, bar 3 of Moment's Notice approached A♭m7 with its
 *  ♭7, a G♭ under E♭maj7: a minor third against the major chord (user-found by
 *  ear, Sep 2026). Inside one key the two scales agree, so a diatonic ii–V–I
 *  walks exactly as before. */
function scaleCands(current: BassInputChord, next: BassInputChord): Approach[] {
  return scaleNeighbours(soundingBass(next), current).map((pc) => ({ pc, label: 'sc' }));
}

/** The nearest tones of `chord`'s scale a step above and a step below `target`
 *  (a half or whole step, above first). A side with no scale tone that close
 *  takes the chromatic neighbour, so there are always two. */
function scaleNeighbours(target: PitchClass, chord: BassInputChord): [PitchClass, PitchClass] {
  const scale = new Set(bassScale(chord.chordType).map((iv) => (chord.rootPc + iv) % 12));
  const step = (dir: 1 | -1): PitchClass => {
    for (const size of [1, 2]) {
      const pc = (((target + dir * size) % 12) + 12) % 12;
      if (scale.has(pc)) return pc as PitchClass;
    }
    return ((((target + dir) % 12) + 12) % 12) as PitchClass;
  };
  return [step(1), step(-1)];
}

/** The smoothest of all three approaches into the next root (the "combining" rung). */
function bestApproach(current: BassInputChord, next: BassInputChord, prev: number): PlacedNote {
  const target = soundingBass(next);
  const dom = dominantApproach(target, prev);
  // Chromatic first (strongest pull), then scale, then the dominant as a fallback.
  return pickApproach(
    [
      ...chromaticCands(target),
      ...scaleCands(current, next),
      { pc: (target + 7) % 12, label: dom.label },
    ],
    prev,
  );
}

/**
 * An *indirect resolution* (Part Two): a two-note enclosure that surrounds the
 * next root before resolving to it on the downbeat. Friedland gives five
 * patterns, each a pair of chromatic/scalar neighbours in a fixed order. We
 * place the pair smoothest from the previous note and pick the pattern with the
 * least total motion — "very strong melodic motion … some interesting new note
 * choices."
 */
function indirectResolution(
  current: BassInputChord,
  next: BassInputChord,
  prev: number,
): [PlacedNote, PlacedNote] {
  const t = soundingBass(next);
  // The enclosure sounds under the chord it leaves, so its scale steps are
  // that chord's (scaleCands).
  const [scAbove, scBelow] = scaleNeighbours(t, current);
  const uchr: Approach = { pc: (t + 1) % 12, label: 'U/chr' };
  const lchr: Approach = { pc: (t + 11) % 12, label: 'L/chr' };
  const usc: Approach = { pc: scAbove, label: 'U/sc' };
  const lsc: Approach = { pc: scBelow, label: 'L/sc' };
  const patterns: [Approach, Approach][] = [
    [lchr, usc], // (1) lower chromatic / upper scalar
    [usc, lchr], // (2) upper scalar / lower chromatic
    [lsc, uchr], // (3) lower scalar / upper chromatic
    [lchr, uchr], // (4) lower chromatic / upper chromatic
    [uchr, lchr], // (5) upper chromatic / lower chromatic
  ];
  let best: [PlacedNote, PlacedNote] | null = null;
  let bestScore = Infinity;
  for (const [a, b] of patterns) {
    const m1 = octaveNearest(a.pc, prev);
    const m2 = octaveNearest(b.pc, m1);
    // Penalise a dead repeat at either step; otherwise reward the least motion.
    let score = Math.abs(m1 - prev) + Math.abs(m2 - m1);
    if (m1 === prev || m2 === m1) score += 24;
    if (score < bestScore) {
      bestScore = score;
      best = [
        { midi: m1, label: a.label },
        { midi: m2, label: b.label },
      ];
    }
  }
  return best!;
}

/** Walk to the nearest chord tone that isn't a repeat of the previous note.
 * With `pickAlt`, take the SECOND-nearest instead — still a smooth chord tone,
 * but a different colour, so a repeated form doesn't play note-for-note twice. */
function walkChordTone(tones: ToneSet, prev: number, pickAlt = false): { midi: number; label: string } {
  const candidates = [
    { pc: tones.third, label: '3' },
    { pc: tones.fifth, label: '5' },
    { pc: tones.seventh, label: tones.seventhLabel },
    { pc: tones.root, label: '8' },
  ];
  const scored = candidates
    .map((c) => {
      const midi = octaveNearest(c.pc, prev);
      return { midi, label: c.label, leap: Math.abs(midi - prev) };
    })
    .filter((c) => c.midi !== prev) // don't just repeat the note
    .sort((a, b) => a.leap - b.leap);
  if (scored.length === 0) return { midi: octaveNearest(tones.fifth, prev), label: '5' };
  const pick = pickAlt && scored.length > 1 ? scored[1] : scored[0];
  return { midi: pick.midi, label: pick.label };
}

interface SlashPedal {
  pc: PitchClass;
  /** One register for the whole run, chosen when its first chord is reached. */
  midi?: number;
}

/**
 * Repeating an explicit slash note beneath DIFFERENT upper harmonies is
 * evidence for a pedal (E♭/B♭ → Fm/B♭ → Gm/B♭ in Moment's Notice). A single
 * inversion, repetitions of the same harmony, an implicit root, or matching
 * basses separated by silence do not make that claim. Inspect only adjacent
 * spans in this performance: never invent a pedal across a form wrap.
 */
function slashPedals(chords: BassInputChord[]): Map<number, SlashPedal> {
  const out = new Map<number, SlashPedal>();
  let start = 0;
  while (start < chords.length) {
    const first = chords[start];
    if (first.bassPc === undefined || first.durationBeats <= 0) {
      start++;
      continue;
    }
    const pc = soundingBass(first);
    let end = start + 1;
    let changesHarmony = false;
    while (end < chords.length) {
      const previous = chords[end - 1];
      const next = chords[end];
      if (
        next.bassPc === undefined || next.durationBeats <= 0 || soundingBass(next) !== pc ||
        Math.abs(next.startBeat - previous.startBeat - previous.durationBeats) > 1e-6
      ) break;
      changesHarmony ||= next.rootPc !== first.rootPc || next.chordType !== first.chordType;
      end++;
    }
    if (changesHarmony) {
      const pedal: SlashPedal = { pc };
      for (let index = start; index < end; index++) out.set(index, pedal);
    }
    start = end;
  }
  return out;
}

/**
 * Build a walking bass line for a form. `chords` is the flattened performance
 * order (one entry per chord, in beats); the line wraps the last chord's
 * approach back to the first root so a looped form joins seamlessly. Returns one
 * note per beat (four feel) or per two beats (two feel), each carrying its
 * Friedland analysis label.
 */
export function generateWalkingBass(
  chords: BassInputChord[],
  opts: WalkingBassOptions,
): BassNote[] {
  if (chords.length === 0) return [];
  const notes: BassNote[] = [];
  let prev = -1; // -1 → no previous note yet; anchor the first root low
  // variation 0 (or omitted) → no alternate picks: the canonical, deterministic
  // line the display shows. Non-zero seeds scatter alternate chord-tone choices.
  const variation = opts.variation ?? 0;
  const varyProb = variation === 0 ? 0 : WALK_VARY_PROB;
  let walkIdx = 0; // counts walking beats, for the spread() variation subset
  const pedals = slashPedals(chords);

  chords.forEach((chord, ci) => {
    const next = chords[(ci + 1) % chords.length];
    const changes =
      next.rootPc !== chord.rootPc ||
      next.chordType !== chord.chordType ||
      soundingBass(next) !== soundingBass(chord);
    const tones = toneSet(chord);
    // This chord's realization. With no timeline these are the global choice
    // for every chord and the loop behaves exactly as it always has.
    const segment = opts.styleAt?.(chord.startBeat);
    const style = segment?.style ?? opts.style;
    const step = (segment?.feel ?? opts.feel) === 'two' ? 2 : 1;

    const pedal = pedals.get(ci);
    if (pedal) {
      // Retain the chosen groove's pulse but let the changing upper chords do
      // the moving. Neither an enclosure nor a fifth belongs inside this bass
      // instruction. Fractional final pulses stop at the harmony boundary.
      const midi = (pedal.midi ??= octaveNearest(pedal.pc, prev < 0 ? ANCHOR_LOW : prev));
      const pulse = style === 'shuffle' ? 1 : style === 'latin' ? 2 : step;
      for (let b = 0; b < chord.durationBeats - 1e-6; b += pulse) {
        const length = Math.min(style === 'latin' ? 1.5 : pulse, chord.durationBeats - b);
        notes.push({ midi, beat: chord.startBeat + b, durationBeats: length, label: 'Pedal' });
        if (style === 'latin' && b + 1.5 < chord.durationBeats - 1e-6) {
          notes.push({
            midi,
            beat: chord.startBeat + b + 1.5,
            durationBeats: Math.min(0.5, chord.durationBeats - b - 1.5),
            label: 'Pedal',
          });
        }
      }
      prev = midi;
      return;
    }

    if (style === 'shuffle') {
      // Classic boogie figure — R 3 5 6 up (8 6 5 3 back down over long
      // chords) — rebuilt from a low root each chord so it keeps its shape.
      // Quarter notes regardless of feel; swing comes from the player.
      const t = bassChordTones(chord.chordType);
      const figure = [0, t.third, t.fifth, 9, 12, 9, t.fifth, t.third];
      const labels = ['R', '3', '5', '6', '8', '6', '5', '3'];
      const quarters = Math.max(1, Math.floor(chord.durationBeats));
      // The register is chosen for the WHOLE figure: a root high enough that
      // the notes this chord plays would climb past E3 drops an octave. Capping
      // each note at E3 instead squashed the 5th, 6th and octave onto an E
      // natural the labels never named.
      const top = Math.max(...figure.slice(0, Math.min(quarters, figure.length)));
      const nearest = octaveNearest(
        tones.bass,
        prev < 0 ? ANCHOR_LOW : Math.min(prev, ANCHOR_LOW + 7),
      );
      const rootLow = nearest + top > BASS_HIGH ? nearest - 12 : nearest;
      for (let s = 0; s < quarters; s++) {
        const midi = rootLow + figure[s % figure.length];
        notes.push({ midi, beat: chord.startBeat + s, durationBeats: 1, label: labels[s % labels.length] });
        prev = midi;
      }
      return;
    }

    if (style === 'latin') {
      // Bossa/samba tumbao: dotted-quarter root + eighth fifth per two-beat
      // cell; the chord's LAST eighth anticipates the next root on a change —
      // the classic Brazilian push. Straight eighths by design.
      const rootMidi = octaveNearest(tones.bass, prev < 0 ? ANCHOR_LOW : prev);
      const fifthMidi = octaveNearest(tones.fifth, rootMidi);
      const duration = Math.max(2, chord.durationBeats);
      for (let b = 0; b + 2 <= duration; b += 2) {
        notes.push({ midi: rootMidi, beat: chord.startBeat + b, durationBeats: 1.5, label: 'R' });
        const lastCell = b + 4 > duration;
        const anticipate = lastCell && changes;
        const offMidi = anticipate ? octaveNearest(soundingBass(next), rootMidi) : fifthMidi;
        notes.push({
          midi: offMidi,
          beat: chord.startBeat + b + 1.5,
          durationBeats: 0.5,
          label: anticipate ? 'R' : '5',
        });
        prev = offMidi;
      }
      return;
    }

    const slots = Math.max(1, Math.floor(chord.durationBeats / step));
    // The "advanced" enclosure spans two slots; the second is queued here.
    let pending: PlacedNote | null = null;

    for (let s = 0; s < slots; s++) {
      const isLast = s === slots - 1;
      let midi: number;
      let label: string;

      if (pending) {
        // Second half of an indirect resolution, computed on the previous beat.
        ({ midi, label } = pending);
        pending = null;
      } else if (s === 0) {
        // The chord's own bottom note always anchors its downbeat — its root,
        // or the slash bass where the symbol names one.
        midi = prev < 0 ? octaveNearest(tones.bass, ANCHOR_LOW) : octaveNearest(tones.bass, prev);
        label = 'R';
      } else if (style === 'advanced' && changes && slots >= 3 && s === slots - 2) {
        // Start a two-note enclosure into the next root; queue its second note.
        const [first, second] = indirectResolution(chord, next, prev);
        pending = second;
        midi = first.midi;
        label = first.label;
      } else if (isLast && changes && style !== 'roots' && style !== 'roots-fifths') {
        // The beat before a chord change sets up the next root with an approach.
        // (For "advanced" this is the fallback when the bar is too short to enclose.)
        const a =
          style === 'chromatic'
            ? pickApproach(chromaticCands(soundingBass(next)), prev)
            : style === 'dominant'
              ? dominantApproach(soundingBass(next), prev)
              : style === 'scale'
                ? pickApproach(scaleCands(chord, next), prev)
                : bestApproach(chord, next, prev);
        midi = a.midi;
        label = a.label;
      } else if (style === 'roots') {
        // Bounce the root and its octave for motion (Friedland's "four" feel roots).
        const low = octaveNearest(tones.bass, ANCHOR_LOW);
        if (s % 2 === 1) {
          midi = Math.min(low + 12, BASS_HIGH);
          label = '8';
        } else {
          midi = low;
          label = 'R';
        }
      } else if (style === 'walking' || style === 'advanced') {
        const pickAlt = spread(walkIdx++, varyProb, variation * 0.37);
        ({ midi, label } = walkChordTone(tones, prev, pickAlt));
      } else {
        // roots-fifths and the inner beats of the approach styles: R 5 8 5 …
        const pick = s % 2 === 1 ? { pc: tones.fifth, label: '5' } : { pc: tones.root, label: '8' };
        midi = octaveNearest(pick.pc, prev);
        label = pick.label;
      }

      notes.push({ midi, beat: chord.startBeat + s * step, durationBeats: step, label });
      prev = midi;
    }
  });

  return notes;
}

export interface EmbellishOptions {
  /** Drop a quiet eighth-note "skip" before selected targets. */
  ghosts: boolean;
  /** Push selected chord-change downbeats an eighth early (the "push"). */
  anticipate: boolean;
  /** Roll a triplet enclosure into selected targets (a "drop"). */
  triplets: boolean;
  /** Skip into selected targets: the eighth before the beat re-plucks the
   *  note before it or sounds the target early, lighter and shorter (the
   *  "de de de-dum" of a walking line). Optional so older callers read false. */
  skips?: boolean;
  /** How often to embellish, 0 (never) … 1 (every eligible spot). With
   *  `bpm`, the fills scale the measured rate for that tempo instead, and
   *  `MEASURED_BASS_AMOUNT` plays it as measured. */
  amount: number;
  /** The tune's tempo. Given, the fills (drops, skips, ghosts) follow how often
   *  walking bassists add notes at that tempo (`extraNoteRates`); absent,
   *  `amount` is each device's share of the beats, as before. */
  bpm?: number;
  /** Per-pass seed shifting which spots get fills, so repeats differ. */
  salt?: number;
  /**
   * Beats where jazz-time devices must NOT fire (#174). A tumbao already IS a
   * rhythm — pushing its roots early or raking ghosts into it destroys the
   * figure — so a Latin span inside an otherwise swinging form opts out here
   * rather than the caller having to skip embellishment for the whole tune.
   *
   * A device is checked at BOTH ends: the target beat and the earlier beat it
   * displaces into. Every one of these devices sounds before its target, so
   * gating only on the target lets a swing push land inside the tumbao it is
   * pushing away from — the exact boundary defect this is meant to prevent.
   */
  skipAt?: (beat: number) => boolean;
}

/** Loudness of ghost/triplet fills relative to a normal note. */
const GHOST_VELOCITY = 0.35;
/** A ghost is a rake, not a note: it stops well before the target. */
const GHOST_GATE = 0.6;
const TRIPLET_VELOCITY = 0.7;
/** A skip is a real note, lighter than the beat and detached into it. */
const SKIP_VELOCITY = 0.6;
const SKIP_GATE = 0.7;
export const PHI = 0.61803398875;

/** The Variation (`amount`) that plays the measured rate of extra notes for
 *  the tempo: the presets' own, so a tune nobody has adjusted walks like the
 *  recordings. Twice it is about the busiest players; zero, plain quarters. */
export const MEASURED_BASS_AMOUNT = 0.35;

/**
 * How often walking bassists add notes inside the beat, by tempo: the Jazz
 * Trio Database (docs/bass-feel.md §1.5), 69,396 walking bars from 1,172
 * recordings, binned by 20 bpm (each row a bin's mean tempo). `single` is the
 * share of beats that take one extra note (a skip or a ghost on the "and"),
 * `double` the share that take two (a triplet drop into the next beat). The
 * drop is a slow-tempo device: under 1% of beats by 180, and taken as none
 * from 200, where the measured traces (a quarter of a percent) are too few to
 * tell from mistimed singles and three notes a beat outrun the fingers.
 */
const EXTRA_NOTE_RATES: readonly { bpm: number; single: number; double: number }[] = [
  { bpm: 106, single: 0.251, double: 0.0323 },
  { bpm: 121, single: 0.249, double: 0.026 },
  { bpm: 140, single: 0.219, double: 0.0173 },
  { bpm: 160, single: 0.216, double: 0.0118 },
  { bpm: 180, single: 0.141, double: 0.0054 },
  { bpm: 200, single: 0.111, double: 0 },
  { bpm: 220, single: 0.104, double: 0 },
  { bpm: 240, single: 0.084, double: 0 },
  { bpm: 260, single: 0.044, double: 0 },
  { bpm: 294, single: 0.039, double: 0 },
];

/** The measured share of beats taking one and two extra notes at `bpm`
 *  (linear between the bins, held flat beyond the ends). */
export function extraNoteRates(bpm: number): { single: number; double: number } {
  const rows = EXTRA_NOTE_RATES;
  if (!(bpm > rows[0].bpm)) return { single: rows[0].single, double: rows[0].double };
  const last = rows[rows.length - 1];
  if (bpm >= last.bpm) return { single: last.single, double: last.double };
  const upper = rows.findIndex((row) => row.bpm > bpm);
  const a = rows[upper - 1];
  const b = rows[upper];
  const t = (bpm - a.bpm) / (b.bpm - a.bpm);
  return {
    single: a.single + t * (b.single - a.single),
    double: a.double + t * (b.double - a.double),
  };
}

/** Each fill's share of the gaps at `bpm`, the measured rates scaled by
 *  `amount` against `MEASURED_BASS_AMOUNT`: a drop takes the two-note share,
 *  the single-note share goes to the skip, or the ghost, or half to each when
 *  both are on. At most 95% of the gaps fill, so the line still walks. */
function fillShares(
  opts: EmbellishOptions,
  bpm: number,
): { drop: number; skip: number; ghost: number } {
  const scale = Math.max(0, opts.amount) / MEASURED_BASS_AMOUNT;
  const { single, double } = extraNoteRates(bpm);
  const drop = opts.triplets ? double * scale : 0;
  const singles = single * scale;
  const skips = !!opts.skips;
  const skip = skips ? (opts.ghosts ? singles / 2 : singles) : 0;
  const ghost = opts.ghosts ? (skips ? singles / 2 : singles) : 0;
  const total = drop + skip + ghost;
  const fit = total > 0.95 ? 0.95 / total : 1;
  return { drop: drop * fit, skip: skip * fit, ghost: ghost * fit };
}

/**
 * Deterministic, evenly-spread selection: returns true for about `amount` of the
 * items, scattered by a golden-ratio low-discrepancy sequence so they never
 * clump — and never use RNG (which would make the line unstable across renders).
 * Different `salt` values pick *different* spread-out subsets at the same density.
 */
export function spread(i: number, amount: number, salt: number): boolean {
  if (amount <= 0) return false;
  if (amount >= 1) return true;
  const frac = ((((i + 1) * PHI + salt) % 1) + 1) % 1;
  return frac < amount;
}

/**
 * Apply rhythmic feel to a finished line. This goes beyond Friedland's
 * straight-quarter method: it adds staples of real jazz-bass time —
 * anticipations (a chord-change root pushed an eighth ahead of the beat),
 * ghost-note skips (a quiet chromatic eighth raked into a target) and triplet
 * "drops" (a quick descending triplet rolled down onto a target). `amount` controls
 * how often each fires; swing is left to the player (it re-times the offbeats
 * these create). Fully deterministic — embellishments are chosen by position.
 */
/**
 * Keep a generated line inside the harmony it walks. A written N.C. bar (or a
 * form ending short of its last bar) is a GAP between chord spans, and the
 * band is out there — yet the devices that lead INTO a chord sound before it:
 * a triplet drop or ghost into the next pass's downbeat, the tumbao's pushed
 * fifth after a one-beat chord, an anticipation. Each landed in Cold Duck
 * Time's two-bar break (user, Sep 2026: "also stop the bass in the NC bars").
 * A note whose onset no chord covers is dropped; a note that runs past its
 * chord's end into a gap is clipped to the chord. A chord followed at once by
 * another is untouched — the next attack damps it as ever — so a gapless line
 * comes back note for note.
 */
export function confineBassToChords<N extends { beat: number; durationBeats: number }>(
  notes: N[],
  spans: { startBeat: number; durationBeats: number }[],
): N[] {
  if (spans.length === 0) return notes;
  const startsAt = (beat: number) => spans.some((s) => Math.abs(s.startBeat - beat) <= 1e-3);
  const out: N[] = [];
  for (const note of notes) {
    const span = spans.find(
      (s) => note.beat >= s.startBeat - 1e-3 && note.beat < s.startBeat + s.durationBeats - 1e-3,
    );
    if (!span) continue;
    const end = span.startBeat + span.durationBeats;
    if (note.beat + note.durationBeats > end + 1e-3 && !startsAt(end)) {
      out.push({ ...note, durationBeats: end - note.beat });
    } else {
      out.push(note);
    }
  }
  return out;
}

export function embellishBassLine(notes: BassNote[], opts: EmbellishOptions): BassNote[] {
  const enabled = opts.ghosts || opts.anticipate || opts.triplets || opts.skips;
  if (notes.length < 2 || !enabled || opts.amount <= 0) return notes;
  const core = notes.map((n) => ({ ...n, velocity: n.velocity ?? 1 }));
  const pushed = new Set<number>(); // indices an anticipation already moved early
  const salt = opts.salt ?? 0; // shifts the spread subsets so passes differ

  // 1) Anticipations — a real chord change (an 'R' whose pitch differs from the
  //    note before it) gets pushed half a beat early, stealing the time from the
  //    preceding note.
  if (opts.anticipate) {
    let eligible = 0;
    for (let i = 1; i < core.length; i++) {
      const n = core[i];
      const before = core[i - 1];
      if (n.label !== 'R' || n.midi === before.midi || n.beat - before.beat < 1) continue;
      // Do not steal the pedal's final half beat to anticipate its release.
      if (before.label === 'Pedal') continue;
      // The push sounds half a beat early, in whatever span that lands in.
      if (opts.skipAt?.(n.beat) || opts.skipAt?.(n.beat - 0.5)) continue;
      if (!spread(eligible++, opts.amount, 0.12 + salt)) continue;
      before.durationBeats = Math.max(0.5, before.durationBeats - 0.5);
      n.beat -= 0.5;
      n.durationBeats += 0.5;
      pushed.add(i);
    }
  }

  // 2) Fills on the full-beat gaps an anticipation left alone — a triplet drop,
  //    a skip or a ghost (mutually exclusive per gap). With a tempo, one spread
  //    draw per gap is split between them by their measured shares; without,
  //    each takes its own spread subset at `amount`.
  const shares = opts.bpm !== undefined && opts.bpm > 0 ? fillShares(opts, opts.bpm) : null;
  const out: BassNote[] = [];
  let gap = 0;
  for (let i = 0; i < core.length; i++) {
    const n = core[i];
    out.push(n);
    const next = core[i + 1];
    if (!next || next.beat - n.beat < 0.99 || pushed.has(i + 1)) continue;
    // A pedal names the whole bass span: chromatic ghosts and triplet drops
    // would walk away from it just as surely as the main walking generator.
    if (n.label === 'Pedal' || next.label === 'Pedal') continue;
    // A fill leads INTO its target but sounds before it: both ends must be
    // clear, or a triplet drop rolls through the end of a tumbao.
    if (opts.skipAt?.(next.beat) || opts.skipAt?.(next.beat - 2 / 3)) continue;
    const g = gap++;
    // A drop DESCENDS (user ruling Sep 2026): the two triplet notes sit a whole
    // step and a half step ABOVE the target and fall onto it, so the landing is
    // the bottom of the roll. The old upper-then-lower enclosure leapt back up
    // into the target, which is an approach figure, not a drop. A target at the
    // top of the register has no room above it, so the gap takes a single
    // instead.
    const canDrop = next.midi + 2 <= BASS_HIGH;
    const draw = shares ? ((((g + 1) * PHI + 0.31 + salt) % 1) + 1) % 1 : 0;
    const dropHere = shares
      ? draw < shares.drop
      : opts.triplets && spread(g, opts.amount, 0.31 + salt);
    // A drop the register refuses becomes the gap's single, whichever is on.
    const single = shares && dropHere && !canDrop ? (shares.skip > 0 ? 'skip' : 'ghost') : null;
    const skipHere = shares
      ? single === 'skip' || (draw >= shares.drop && draw < shares.drop + shares.skip)
      : opts.skips && spread(g, opts.amount, 0.47 + salt);
    const ghostHere = shares
      ? single === 'ghost' ||
        (draw >= shares.drop + shares.skip && draw < shares.drop + shares.skip + shares.ghost)
      : opts.ghosts && spread(g, opts.amount, 0.61 + salt);
    if (opts.triplets && canDrop && dropHere) {
      // A triplet that LANDS on the target: the pair fills the beat right before
      // `next`, so `next` is the triplet's third hit and sounds immediately —
      // even across a half-note (two-feel) gap, where the held note rings first
      // and the triplet rolls in at the end.
      n.durationBeats = Math.max(0.25, Math.min(n.durationBeats, next.beat - 2 / 3 - n.beat));
      out.push({ midi: next.midi + 2, beat: next.beat - 2 / 3, durationBeats: 1 / 3, label: 't', velocity: TRIPLET_VELOCITY });
      out.push({ midi: next.midi + 1, beat: next.beat - 1 / 3, durationBeats: 1 / 3, label: 't', velocity: TRIPLET_VELOCITY });
    } else if (opts.skips && skipHere) {
      // A skip on the "and" before the beat (the player swings it onto the
      // kit's skip note): the note before it plucked again, or the target
      // sounded early, chosen by position so the line is the same every time.
      const repeat = spread(g, 0.5, 0.83 + salt);
      n.durationBeats = Math.max(0.25, Math.min(n.durationBeats, next.beat - 0.5 - n.beat));
      out.push({
        midi: repeat ? n.midi : next.midi,
        beat: next.beat - 0.5,
        durationBeats: 0.5,
        label: 's',
        velocity: SKIP_VELOCITY,
        articulationGate: SKIP_GATE,
      });
    } else if (opts.ghosts && ghostHere) {
      const ghostPc = (((next.midi - 1) % 12) + 12) % 12;
      out.push({
        midi: octaveNearest(ghostPc, next.midi),
        beat: next.beat - 0.5,
        durationBeats: 0.5,
        label: 'g',
        velocity: GHOST_VELOCITY,
        articulationGate: GHOST_GATE,
      });
    }
  }
  return out.sort((a, b) => a.beat - b.beat);
}
